import React, { useState, useEffect } from 'react';
import { Menu } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
//import styles from '../styles/RequestGroupDropdown.module.scss';
import styles from '../styles/SystemDropdown.module.scss';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getRequestGroupUrl } from '../selectors';
import service from 'util/apiService';
import SourceIcon from '@mui/icons-material/Source';
import SubdirectoryArrowRightOutlinedIcon from '@mui/icons-material/SubdirectoryArrowRightOutlined';
import { getUserAccess } from 'modules/cbre-flow-api-ducks/selectors';

interface RequestItem {
  work_requestId: string;
  wr_group: string;
  wr_group_formatted: string;
  description: string;
  c_requesttype: string;
  wr_number: string;
  wo_number: string;
  number: string;
}

interface RequestGroupDropdownProps {
  requestId?: string;
  groupId?: string;
  currentRequestId?: string;
  onRequestSelect?: (requestId: string) => void;
  initialRequests?: RequestItem[];
  requestGroupFormatted?: string;
}

const RequestGroupDropdown: React.FC<RequestGroupDropdownProps> = ({
  requestId,
  groupId = '2000',
  currentRequestId,
  onRequestSelect,
  initialRequests,
  requestGroupFormatted,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [requests, setRequests] = useState<RequestItem[]>(
    initialRequests || [],
  );
  const [loading, setLoading] = useState<boolean>(!initialRequests);
  const [error, setError] = useState<string | null>(null);

  // Get the API URL from the Redux selector
  const requestGroupUrl = useSelector(getRequestGroupUrl);
  const { site } = useSelector(getUserAccess) || {};


  const open = Boolean(anchorEl);
  const history = useHistory();

  // Fetch request group data using the URL from the selector
  useEffect(() => {
    if (!requestId || !requestGroupUrl || initialRequests) return;

    setLoading(true);
    service
      .get(`${requestGroupUrl}/${requestId}?groupId=${groupId}&site=${site?.site || ''}`)
      .then((response) => {
        if (response.data && Array.isArray(response.data)) {
          setRequests(response.data);
        } else if (response.data && Array.isArray(response.data.data)) {
          // Handle nested data structure if API returns { data: [...] }
          setRequests(response.data.data);
        } else {
          setError('Invalid response format');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching request group data:', err);
        setError('Failed to load request group data');
        setLoading(false);
      });
  }, [requestId, groupId, requestGroupUrl, initialRequests]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRequestClick = (request: RequestItem) => {
    handleClose();

    if (onRequestSelect) {
      onRequestSelect(request.work_requestId);
    } else {
      // Navigate to request details page by default
      history.push(`/elis/requests/details/${request.work_requestId}`);
    }
  };

  // Get the formatted group name from the first item if available
  const groupName =
    requestGroupFormatted ||
    (requests.length > 0 ? requests[0].wr_group_formatted : `RG ${groupId}`);

  // Don't show dropdown if there's no data and not loading
  if (!loading && requests.length === 0) {
    return <span className={styles.emptySystem}>--</span>;
  }

  return (
    <div className={styles.systemContainer}>
      <div
        className={styles.systemButton}
        onClick={handleClick}
        aria-controls={open ? 'request-group-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <SourceIcon className={styles.systemIcon} />
        <span className={styles.systemName}>{groupName}</span>
        <KeyboardArrowDownIcon
          className={`${styles.arrowIcon} ${open ? styles.expanded : ''}`}
        />
      </div>

      <Menu
        id="request-group-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'request-group-button',
          sx: { padding: 0 },
        }}
        PaperProps={{
          className: styles.dropMenuSystem,
          sx: {
            maxWidth: '350px',
            borderRadius: '4px',
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        <div className={styles.title}>Requests in this group:</div>

        {loading ? (
          <div className={styles.loading}>Loading requests...</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : (
          <div className={styles.content}>
            {requests.map((request) => (
              <div
                key={request.work_requestId}
                className={`${styles.menuItem} ${currentRequestId === request.work_requestId ? styles.active : ''}`}
                onClick={() => handleRequestClick(request)}
              >
                <div className={styles.requestInfo}>
                  <div className={styles.label}>
                    <SubdirectoryArrowRightOutlinedIcon
                      className={styles.instrumentIcon}
                    />
                    <span className={styles.label_name}>
                    {`${request.wr_number ? 'SR#' + request.wr_number : ''}`}
                    </span>
                    
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Menu>
    </div>
  );
};

export default RequestGroupDropdown;
